import axios from "axios";
import { mapGetters } from "vuex";
import Swal from "sweetalert2";
import { QueryBuilder } from "../utils/queryBuilder";

export default {
    data() {
        return {
            defaultInputConfig: {
                horizontal: {
                    label: "col-sm-2",
                    input: "col-sm-10",
                },
                size: "sm",
                class: "font-weight-bold",
            },
            defaultSwitchConfig: {
                horizontal: {
                    label: "col-sm-3",
                    input: "col-sm-9",
                },
                size: "sm",
                class: "font-weight-bold",
            },
            defaultSelectConfig: {
                horizontal: {
                    label: "col-sm-2",
                    input: "col-sm-10",
                },
                placeholder: "Select an option",
                invalidFeedback: "Field is required",
                size: "sm",
                class: "font-weight-bold",
            },
            defaultSelectInTableConfig: {
                horizontal: false,
                placeholder: "Select an option",
                invalidFeedback: "Field is required",
                size: "sm",
                class: "font-weight-bold",
            },
        };
    },

    messages: {
        create: {
            header: "¡_save!",
            body: "saved_successfully",
            color: "success",
        },
        update: {
            header: "¡_update!",
            body: "changes_have_been_saved_successfully",
            color: "success",
        },
        success: {
            header: "¡_success!",
            body: "action_completed_successfully",
            color: "success",
        },
        delete: {
            header: "¡_delete!",
            body: "successfully_removed",
            color: "success",
        },
        error: {
            header: "¡Error!",
            body: "There was a problem",
            color: "danger",
        },
    },

    computed: {
        ...mapGetters({
            storeUser: "auth/user",
            storeGlobalFields: "global/fields",
            storeGlobalFieldsAdvance: "global/fieldsAdvance",
            storeGlobalSubTags: "global/subTags",
        }),
        UserRoles() {
            return this.storeUser ? this.storeUser.groups : [];
        },
        ApiCore() {
            return process.env.VUE_APP_API_CORE;
        },
        ApiSass() {
            return process.env.VUE_APP_API_SASS;
        },
    },

    methods: {
        generateUUID() {
            function uuidv4() {
                return ([1e7] + -1e3 + -4e3 + -8e3 + -1e11).replace(
                    /[018]/g,
                    (c) =>
                        (
                            c ^
                            (crypto.getRandomValues(new Uint8Array(1))[0] &
                                (15 >> (c / 4)))
                        ).toString(16)
                );
            }
            return uuidv4();
        },

        replaceURLParams(url, params) {
            for (const key in params) {
                if (Object.hasOwnProperty.call(params, key)) {
                    const element = params[key];
                    url = url.replace(`:${key}`, element);
                }
            }
            return url;
        },

        isAuthorized(roles) {
            let authorized = false;
            roles.forEach((role) => {
                if (this.UserRoles.includes(role)) {
                    authorized = true;
                }
            });
            return authorized;
        },

        hasNextItem({ index, array }) {
            return index < array.length - 1;
        },

        async mapFetcher(collection) {
            let resolves = [];
            for (let i = 0; i < collection.length; i++) {
                const fetcherConfig = collection[i];
                let resolve = await this.fetcher(fetcherConfig);
                resolves.push(resolve);
            }
            return resolves;
        },

        async fetcher({
            url,
            method = "GET",
            params,
            data,
            query = {},
            baseURL,
            headers,
        }) {
            /** Replace params in URL */
            for (const key in params) {
                if (Object.hasOwnProperty.call(params, key)) {
                    const element = params[key];
                    url = url.replace(`:${key}`, element);
                }
            }

            for (const key in query) {
                if (Object.hasOwnProperty.call(query, key)) {
                    if (!query[key] || query[key] == "") {
                        delete query[key];
                    }
                }
            }

            let axiosConfig = {
                url,
                method,
                params: query,
                data,
                headers,
            };

            if (baseURL) {
                axiosConfig = {
                    ...axiosConfig,
                    baseURL,
                };
            }

            try {
                const response = await axios(axiosConfig);
                return response.data;
            } catch (error) {
                throw error;
            }
        },

        async deleteData({ url, data, fetch }) {
            try {
                let result = await Swal.fire({
                    title: this.$t("are_you_sure?"),
                    text: this.$t("you_will_not_be_able_to_revert_this!"),
                    icon: "warning",
                    showCancelButton: true,
                    confirmButtonColor: "#3085d6",
                    cancelButtonColor: "#d33",
                    confirmButtonText: this.$t("yes,_delete_it!"),
                });
                if (result.isConfirmed) {
                    let response = await this.fetcher({
                        url,
                        method: "DELETE",
                        data,
                        ...fetch,
                    });

                    this.showMessageDelete();

                    return {
                        status: true,
                        response,
                    };
                }
            } catch (error) {
                this.showMessageError(error);
                throw error;
                return {
                    status: false,
                };
            }
        },

        async showMessageError(error) {
            let message = this.$options.messages.error;
            let header = message.header;
            let body = message.body;

            if (error) {
                if (
                    error.response &&
                    error.response.data &&
                    error.response.data.error
                ) {
                    body = error.response.data.error;
                } else if (
                    error.response &&
                    error.response.data &&
                    error.response.data.message
                ) {
                    body = error.response.data.message;
                } else if (error.message) {
                    body = error.message;
                } else {
                    body = error;
                }

                this.$store.dispatch("toast/addFixedToasts", {
                    ...message,
                    header,
                    body,
                });
                // console.error(error);
            } else {
                this.$store.dispatch("toast/addFixedToasts", {
                    ...message,
                    header,
                    body,
                });
            }
        },

        isFunction(functionToCheck) {
            return functionToCheck && functionToCheck instanceof Function;
        },

        async showMessage({ header, body, color }) {
            this.$store.dispatch("toast/addFixedToasts", {
                header,
                body,
                color,
            });
        },

        async showMessageSuccess() {
            let message = this.$options.messages.success;
            await this.showMessage(message);
        },

        async showMessageDelete() {
            let message = this.$options.messages.delete;
            await this.showMessage(message);
        },

        normalizeObject(object, deleteNotValidValues = false) {
            for (const key in object) {
                if (Object.hasOwnProperty.call(object, key)) {
                    const item = object[key];
                    if (item == null || item == undefined) {
                        if (deleteNotValidValues) {
                            delete object[key];
                        } else {
                            object = {
                                ...object,
                                [key]: "",
                            };
                        }
                    }
                }
            }

            return object;
        },

        normalizeArrayData({ data }) {
            if (Array.isArray(data)) {
                return data.map((item) => {
                    return this.normalizeObject(item);
                });
            } else {
                return [];
            }
        },

        async loadFields() {
            let data = [];
            if (this.storeGlobalFields.length <= 0) {
                let response = await axios.get("/field");
                data = response.data;
                this.$store.dispatch("global/saveFields", { fields: data });
            }
        },
        copyObject(object) {
            return JSON.parse(JSON.stringify(object));
        },

        async loadFieldsAdvance() {
            let data = [];
            if (this.storeGlobalFieldsAdvance.length <= 0) {
                let response = await axios.get("/fields", {
                    baseURL: this.ApiCore,
                });
                data = response.data._items;
                this.$store.dispatch("global/saveFieldsAdvance", {
                    fields: data,
                });

                let fieldsIndexById  = {};
                for (let i = 0; i < data.length; i++) {
                    const field = data[i];
                    fieldsIndexById = {
                        ...fieldsIndexById,
                        [field.id]: field
                    }
                }

                this.$store.dispatch("global/saveFieldsAdvanceIndexById", {
                    fieldsIndexById: fieldsIndexById,
                });
                
            }
        },

        async loadSubTags() {
            let data = [];
            if (this.storeGlobalSubTags.length <= 0) {
                let response = await axios.get("/sub-tags", {
                    baseURL: this.ApiCore,
                });
                data = response.data._items;
                this.$store.dispatch("global/saveSubTags", { subTags: data });
            }
        },

        getSubTagById(id) {
            let find = this.storeGlobalSubTags.find((item) => {
                return item.id === id;
            });

            return find;
        },

        beautifyList(list) {
            let beautify = list;
            beautify = beautify.replace(/\n/g, "").replace(/,/g, ",\n");
            return beautify;
        },

        noBeautifyList(list) {
            let noBeautify = list;
            noBeautify = noBeautify.replace(/\n/g, "");
            return noBeautify;
        },
        listToArray(list) {
            let array = [];
            if (list) array = this.noBeautifyList(list).split(",");
            return array;
        },
        intToString(value) {
            return value.toString();
        },
        stringToBoolean(value) {
            if (typeof value === "string") {
                if (value === "0") value = false;
                else if (value === "1") value = true;
            }
            return Boolean(value);
        },
        calculatePercentage(num1, num2) {
            return num2 != 0 ? (num1 / num2) * 100 : 0;
        },
        jsonDecode(value) {
            return JSON.parse(value);
        },

        isString(value) {
            return typeof value === "string" || value instanceof String;
        },

        generateStringRamdom(length = 8) {
            let stringRandom = "";
            let str =
                "ABCDEFGHIJKLMNOPQRSTUVWXYZ" +
                "abcdefghijklmnopqrstuvwxyz0123456789@#$";

            for (let i = 1; i <= length; i++) {
                var char = Math.floor(Math.random() * str.length + 1);

                stringRandom += str.charAt(char);
            }

            return stringRandom;
        },

        async downloadFileFromURL(url, name = "(No name)") {
            let elementLink = document.createElement("a");
            elementLink.setAttribute("href", url);
            elementLink.setAttribute("download", name);
            elementLink.click();
        },

        updatePageLoading(value) {
            this.$store.dispatch("page/updateLoading", {
                loading: value,
            });
        },

        applyQueryToRoute({ route, query }) {
            const queryBuilder = new QueryBuilder(query);

            return route + "?query=" + queryBuilder.toString();
        },
    },
};
